function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
// ----------------------------------------------------------------------------
// Web Component: iamCart <iam-cart>
// Extends: HTMLElement
// ----------------------------------------------------------------------------
// The central web component for managing the Cart Form (i.e. cart page)
// - It manages and coordinates the form initialization, product variant selection,
//   quantity changes, and form validation.
// - Sub-components are used to manage the various interactive form elements, but
//   this component centralizes the form state itself (e.g. the quantity selector
//   manages the qty input field UX, but this component is the source of truth for
//   the resulting quantity selected; similarly for variant selection, etc.)
// - It handles form submission over AJAX (i.e. when add-to-cart button triggers
//   the form submission, this component manages the submission).
// ----------------------------------------------------------------------------
// Customization:
// - Modify the `product-form.graphql` query to pull any custom product/variant
//   data that the form (or child elements) require, such as metafields
// - Import optional extensions to add additional functionality to the form
//   (e.g. sticky form, sale indicator, quantity limits, etc.)
// ----------------------------------------------------------------------------
// Event Listeners:
// - `formValidation:init`            → Initializes form validation when the validation
//                                      class is ready
// - `cart:changed`            → Listens for when the form data changes
//                                      (option, selected variant, quantity, property)
//                                      to update the form validation state
// ----------------------------------------------------------------------------
// Event Triggers:
// - `cart:init`               → Triggers when the product form is ready
//                                      for modules/extensions to hook in
// - `cart:validation:init`    → Triggers when the form validation is initialized
//                                      (allowing modules to extend validation)
// - `cart:validation:changed` → Triggers when the form validation state changes
// - `cart:validation:success` → Triggers when the form validation is successful
// - `cart:validation:failed`  → Triggers when the form validation fails
// - `cart:changed`            → Triggers when the form data changes
//                                      (any of option, selected variant, quantity, property)
// - `cart:option:changed`     → Triggers when the form data changes (option)
// - `cart:variant:changed`    → Triggers when the form data changes (selected variant)
// - `cart:quantity:changed`   → Triggers when the form data changes (quantity)
// - `cart:property:changed`   → Triggers when the form data changes (property)
// - `cart:submitting`         → Triggers when the form is being submitted
// - `cart:submitted:success`  → Triggers when the form is successfully submitted
// - `cart:submitted:error`    → Triggers when the form submission fails
// ----------------------------------------------------------------------------
// Event Filters:
// - `cart:submitting:data`    → Allows filtering of the form data before submission
// ----------------------------------------------------------------------------
// Key Data Mutations:
// - Fetches product and custom data (product-form.graphql) and initializes the Shopify `cartForm`
// - Toggles form loading and error states based on child components and validation states
// - Submits the form over AJAX after validation
// ----------------------------------------------------------------------------
// Gotchas:
// - Relies on promises to fetch product and custom data, which may fail if the API calls do not resolve successfully.
// - Custom product data is only fetched if a query is provided.
// - Error messages are injected into the `.form__errors-container` element
//   and the form is marked with a `--error` class until resolved
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Helpers
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import translate from 'scripts/helpers/iamota-translate';
import iamForm from 'scripts/classes/iamForm';

// Styles
//import './_cart.scss';

// Elements
import './cart-items/cart-items';
import './cart-undo/cart-undo';
import './cart-summary/cart-summary';
import './cart-footer/cart-footer';
import '../free-shipping-progress/free-shipping-progress';

// -------------------------------------
// Web Component
// -------------------------------------

class iamCartForm extends iamForm {
  /**
   * Initialize the form
   */
  connectedCallback() {
    var _this$dataset;
    super.connectedCallback();

    // Variables
    this.cart = (_this$dataset = this.dataset) !== null && _this$dataset !== void 0 && _this$dataset.cartData ? JSON.parse(this.dataset.cartData) : null;

    // Validate Required Attributes
    if (!this.validateRequiredAttributes(['cart'])) return;

    // Bind Event Listeners
    iamotaEventHandler.subscribe('cart:modified', this.onCartModified.bind(this), {
      source: "cart.js"
    });
    this.addEventListener('submit', this.onFormSubmit.bind(this));

    // Initialize the form
    iamotaEventHandler.trigger('cartForm:init', {
      iamForm: this
    }, {
      context: this,
      isAtomic: true
    });
  }
  onCartModified(_ref) {
    var cart = _ref.cart;
    this.cart = cart;
  }

  /**
   * Handle form submission
   * @param {*} event
   * @returns
   */
  onFormSubmit(event) {
    var _this = this;
    return _asyncToGenerator(function* () {
      // Block the native form submission behavior
      event.preventDefault();

      // Block submitting the form in an invalid state
      if (!(yield _this.validateForm())) {
        _this.setErrorState(translate('Unable to submit the form with errors', 'cartForm.error_validation'));
        return;
      }

      // Add the "submit" button data and continue with the form submission
      if (event.submitter && event.submitter.name) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = event.submitter.name;
        input.value = event.submitter.value || '';
        event.target.appendChild(input);
      }
      event.target.submit();
    })();
  }
}
customElements.define('iam-cart-form', iamCartForm);